// İSG Portal — Kontrol İtemları (Control Items — tenant-scoped) katalog API çağrıları + tipleri.
// **İSG uzmanı / firma admin tarafı** (web-expert): firma kendi kontrol itemı kataloğunu
// (`ControlItem`) tanımlar — genel (bizim) varsayılan checklist + firma özel öge. Her kontrol itemı
// bir **iş izni tipine** (§22) bağlanabilir (`PermitTypeControlItem`); izinde **İSG uzmanı** her
// kontrol itemını işaretler (`WorkPermitControlCheck`; §25.3, `permits.ts` `setControlCheck`).
// **Görünürlük (D4):** kontrol itemları İSG uzmanı **iç checklist**'idir — taşeron (supplier) GÖRMEZ.
//
// KAYNAK: api-contract.md §25 (Kontrol İtemları). Backend kanonu; burada **varsayım YOK** —
// endpoint yolları, istek/yanıt alanları ve kimlik kuralları (Result unwrap `value`; Create →
// `value.uuid` + 201; liste → `value` + `total`/`filteredTotal`) doğrudan sözleşmedendir.
// Serileştirme camelCase (§1).
//
// **Tenant izolasyonu (§4b/§25).** §24 ile aynı: çağıranın firmasına kapanır; istemci firma kimliği
// GÖNDERMEZ; başka firmanın ögesi yok gibi `404 CONTROL_ITEM_NOT_FOUND`. Silme **soft-delete**
// (tipe önceden bağlanmış izinler etkilenmez). Katalog + tip ataması `[ModuleAction("permit-types",
// …)]` (KKD ile aynı grant — yeni grant gerekmez); izinde işaretleme `permits` (§25.3). Ayrı
// tek-segment route (`/control-items`).

import { api } from './client';
import type { Empty } from './envelope';

/**
 * Kontrol itemı — api-contract §25 (`ControlItemDto`). `text` = kontrol ifadesi/sorusu. `isDefault`
 * = genel varsayılan checklist ögesi mi (**istemciden set EDİLMEZ** — yalnız seed `true`). Genel öge
 * de düzenlenebilir/pasifleştirilebilir. Sıralı iç `Id` istemciye **sızmaz** (yalnız `uuid`).
 * `createdAt` UTC ISO (§4).
 */
export interface ControlItemDto {
  uuid: string;
  text: string;
  description: string | null;
  /** Genel (bizim) varsayılan checklist ögesi mi — **istemciden set edilmez**; yalnız rozet/görünüm (§25). */
  isDefault: boolean;
  isActive: boolean;
  createdAt: string | null;
}

/**
 * Kontrol itemı oluşturma isteği — api-contract §25.1 (`POST /control-items`). `text` zorunlu (boş →
 * `400 VALIDATION_FAILED`); yeni öge `isActive = true`, `isDefault = false` (istekte taşınmaz).
 * **`isDefault` GÖNDERİLMEZ.**
 */
export interface ControlItemCreateRequest {
  text: string;
  description?: string | null;
}

/**
 * Kontrol itemı güncelleme isteği — api-contract §25.1 (`PUT /control-items/{uuid}`). Metin/açıklama/
 * aktiflik güncellenir; **`isDefault` istemciden set edilmez** (korunur — genel öge de düzenlenebilir).
 */
export interface ControlItemUpdateRequest {
  text: string;
  description?: string | null;
  isActive: boolean;
}

/**
 * Kontrol itemı liste/filtre parametreleri — api-contract §25.1 (`GET /control-items`) + §3 paging.
 * `isActive` (verilmezse hepsi), `search` (metin substring, case-insensitive). Sıra backend'de
 * **oluşturma sırası** (checklist sırası anlamlı; iç `Id` artan) — istemci sort göndermez.
 */
export interface ControlItemListParams {
  isActive?: boolean;
  search?: string;
  page?: number;
  pageSize?: number;
}

/** Boş/whitespace dizeyi `undefined`'a indirger (searchParams'ta atlanır). */
function nonEmpty(value: string | undefined): string | undefined {
  return value && value.trim().length > 0 ? value.trim() : undefined;
}

/**
 * `GET /control-items` (`list`) — firmanın kontrol kataloğu (sayfalı + filtreli). Sıra oluşturma
 * sırası (istemci sort göndermez). `isActive` verilmezse hepsi döner.
 */
export function listControlItems(params: ControlItemListParams = {}) {
  return api.list<ControlItemDto>('control-items', {
    searchParams: {
      isActive: params.isActive,
      search: nonEmpty(params.search),
      page: params.page,
      pageSize: params.pageSize,
    },
  });
}

/**
 * `POST /control-items` (`create`) — kontrol itemı oluşturur (`isActive = true`,
 * `isDefault = false`); yeni kimlik `value.uuid` (201). `text` boş → `400 VALIDATION_FAILED`.
 */
export function createControlItem(body: ControlItemCreateRequest) {
  return api.post<ControlItemDto>('control-items', { json: body });
}

/**
 * `PUT /control-items/{uuid}` (`update`) — metin/açıklama/aktiflik günceller (`isDefault` korunur).
 * Yok/kapsam dışı → `404 CONTROL_ITEM_NOT_FOUND`.
 */
export function updateControlItem(uuid: string, body: ControlItemUpdateRequest) {
  return api.put<ControlItemDto>(`control-items/${uuid}`, { json: body });
}

/**
 * `DELETE /control-items/{uuid}` (`delete`) — ögeyi **soft-delete** eder (tipe önceden bağlanmış
 * izinler etkilenmez). Yok/kapsam dışı → `404 CONTROL_ITEM_NOT_FOUND`.
 */
export function deleteControlItem(uuid: string) {
  return api.delete<Empty>(`control-items/${uuid}`);
}
